import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Paper,
  Grid,
  Typography,
  Box,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  Timeline as TimelineIcon,
  Chat as ChatIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { botService, whatsappService, logService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const BotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [qrCode, setQrCode] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  const { data: botData, isLoading: botLoading } = useQuery(
    ['bot', id],
    async () => {
      const response = await botService.get(id!);
      return response.data;
    }
  );

  const { data: logsData } = useQuery(
    ['logs', id, page, rowsPerPage],
    async () => {
      const response = await logService.getLogs(id!, rowsPerPage, page * rowsPerPage);
      return response.data;
    },
    { enabled: tabValue === 1 }
  );

  const { data: statsData } = useQuery(
    ['stats', id],
    async () => {
      const response = await logService.getStats(id!);
      return response.data;
    },
    { enabled: tabValue === 2 }
  );

  const toggleBotMutation = useMutation(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      botService.toggle(id, isActive),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bot', id]);
      },
    }
  );

  const deleteBotMutation = useMutation(
    (id: string) => botService.delete(id),
    {
      onSuccess: () => {
        navigate('/bots');
      },
    }
  );

  const connectWhatsApp = async () => {
    setIsConnecting(true);
    setQrCode('');
    
    try {
      await whatsappService.connect(id!);
      
      const checkQR = setInterval(async () => {
        try {
          const response = await whatsappService.getQR(id!);
          if (response.data.qrCode) {
            setQrCode(response.data.qrCode);
            clearInterval(checkQR);
          }
        } catch (error) {
          console.log('Aguardando QR Code...');
        }
      }, 2000);

      const checkStatus = setInterval(async () => {
        try {
          const response = await whatsappService.getStatus(id!);
          if (response.data.isConnected && response.data.phoneNumber) {
            clearInterval(checkStatus);
            clearInterval(checkQR);
            setQrCode('');
            queryClient.invalidateQueries(['bot', id]);
          }
        } catch (error) {
          console.log('Verificando conexão...');
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(checkQR);
        clearInterval(checkStatus);
        setIsConnecting(false);
      }, 300000);
    } catch (error: any) {
      console.error('Erro ao conectar WhatsApp:', error);
    }
  };

  const disconnectWhatsApp = async () => {
    const confirmMessage = 
      'Tem certeza que deseja desconectar o WhatsApp?\n\n' +
      '⚠️ Isso irá:\n' +
      '• Desconectar completamente o número\n' +
      '• Remover todas as sessões salvas\n' +
      '• Exigir novo QR Code para reconectar\n\n' +
      'Esta ação não pode ser desfeita.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    console.log('Iniciando desconexão do WhatsApp para bot:', id);
    try {
      const response = await whatsappService.disconnect(id!);
      console.log('Resposta da desconexão:', response);
      
      await queryClient.invalidateQueries(['bot', id]);
      setQrCode('');
      setIsConnecting(false);
      
      // Aguardar um momento antes de permitir nova conexão
      setTimeout(() => {
        queryClient.invalidateQueries(['bot', id]);
      }, 2000);
      
      console.log('WhatsApp desconectado com sucesso');
      alert('WhatsApp desconectado com sucesso! Será necessário escanear um novo QR Code para reconectar.');
    } catch (error: any) {
      console.error('Erro ao desconectar WhatsApp:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      alert(`Erro ao desconectar WhatsApp: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleToggleBot = async () => {
    const bot = botData?.bot;
    if (bot) {
      // Se está tentando ativar e não tem WhatsApp conectado
      if (!bot.is_active && !bot.phone_number) {
        alert('Você precisa conectar um WhatsApp antes de ativar o bot.');
        return;
      }
      
      try {
        await toggleBotMutation.mutateAsync({
          id: bot.id,
          isActive: !bot.is_active,
        });
      } catch (error: any) {
        // Mostrar erro do servidor se houver
        if (error.response?.data?.error) {
          alert(error.response.data.error);
        }
      }
    }
  };

  const handleDeleteBot = async () => {
    if (window.confirm('Tem certeza que deseja excluir este bot?')) {
      await deleteBotMutation.mutateAsync(id!);
    }
  };

  const shareQRCode = async () => {
    try {
      const response = await botService.createShareLink(id!);
      const link = response.data.shareLink;
      
      // Copiar para clipboard
      await navigator.clipboard.writeText(link);
      setShowShareSuccess(true);
      
      // Ocultar mensagem de sucesso após 5 segundos
      setTimeout(() => setShowShareSuccess(false), 5000);
    } catch (error) {
      console.error('Erro ao criar link de compartilhamento:', error);
      alert('Erro ao criar link de compartilhamento');
    }
  };

  if (botLoading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  const bot = botData?.bot;

  if (!bot) {
    return (
      <Container>
        <Alert severity="error">Bot não encontrado</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/bots')}
          sx={{ mr: 2 }}
        >
          Voltar
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {bot.name}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ChatIcon />}
          onClick={() => navigate(`/bots/${id}/chat`)}
          sx={{ mr: 2 }}
        >
          Chat
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/bots/${id}/edit`)}
          sx={{ mr: 2 }}
        >
          Editar
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={bot.is_active}
              onChange={handleToggleBot}
              disabled={toggleBotMutation.isLoading || (!bot.is_active && !bot.phone_number)}
            />
          }
          label={
            <Box>
              <Typography>Ativo</Typography>
              {!bot.phone_number && (
                <Typography variant="caption" color="error">
                  Conecte o WhatsApp primeiro
                </Typography>
              )}
            </Box>
          }
          sx={{ mr: 2 }}
        />
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteBot}
          disabled={deleteBotMutation.isLoading}
        >
          Excluir
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações do Bot
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Box display="flex" gap={1} mt={0.5}>
                  {bot.is_active ? (
                    <Chip label="Ativo" color="success" size="small" />
                  ) : (
                    <Chip label="Inativo" size="small" />
                  )}
                  {bot.phone_number && (
                    <Chip
                      icon={<WhatsAppIcon />}
                      label="Conectado"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Chave OpenAI
                </Typography>
                <Typography variant="body1">{bot.openai_key}</Typography>
              </Box>
              {bot.assistant_name && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Assistente
                  </Typography>
                  <Typography variant="body1">{bot.assistant_name}</Typography>
                </Box>
              )}
              {bot.phone_number && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    WhatsApp
                  </Typography>
                  <Typography variant="body1">{bot.phone_number}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conexão WhatsApp
              </Typography>
              {!bot.phone_number ? (
                <Box>
                  {!isConnecting && !qrCode && (
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Conecte este bot ao WhatsApp para começar a receber mensagens
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        onClick={connectWhatsApp}
                      >
                        Conectar WhatsApp
                      </Button>
                    </Box>
                  )}

                  {isConnecting && !qrCode && (
                    <Box textAlign="center" py={3}>
                      <CircularProgress />
                      <Typography variant="body1" sx={{ mt: 2 }}>
                        Gerando QR Code...
                      </Typography>
                    </Box>
                  )}

                  {qrCode && (
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Escaneie o QR Code com seu WhatsApp
                      </Typography>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <img src={qrCode} alt="QR Code" style={{ maxWidth: '250px' }} />
                      </Box>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<ShareIcon />}
                          onClick={shareQRCode}
                          size="small"
                        >
                          Compartilhar QR Code
                        </Button>
                      </Box>
                      {showShareSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Link copiado para área de transferência! Válido por 10 minutos.
                        </Alert>
                      )}
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Aguardando conexão...
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    WhatsApp conectado: {bot.phone_number}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={disconnectWhatsApp}
                  >
                    Desconectar WhatsApp
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Visão Geral" />
          <Tab label="Logs de Mensagens" />
          <Tab label="Estatísticas" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="body1" color="textSecondary">
            Selecione uma aba para ver mais informações
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Remetente</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Resposta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsData?.logs?.map((log: any) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell>{log.sender}</TableCell>
                    <TableCell>{log.message}</TableCell>
                    <TableCell>{log.response}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {logsData?.logs?.length === 0 && (
            <Box textAlign="center" py={4}>
              <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Nenhuma mensagem registrada ainda
              </Typography>
            </Box>
          )}
          <TablePagination
            component="div"
            count={logsData?.total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Registros por página"
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {statsData?.stats?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={statsData.stats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="messages_per_day"
                  stroke="#8884d8"
                  name="Mensagens"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <TimelineIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Sem dados suficientes para exibir estatísticas
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default BotDetail;